import InfraMaintenanceScreen from '@/components/infra-maintenance/infra-maintenance-screen';
import { NativeShopShellReady } from '@/components/common/native-shop-shell-ready';
import {
  useApiHealth,
  type ApiHealthPollMode,
} from '@/framework/use-api-health';
import { isApiHealthGateEnabled } from '@/lib/api-health/is-api-health-gate-enabled';
import { isApiReady } from '@/lib/api-health/is-api-ready';
import { API_HEALTH_FAILURE_THRESHOLD } from '@/lib/api-health/types';
import { useEffect, useRef, useState } from 'react';

type ApiHealthGateProps = {
  children: React.ReactNode;
};

function getPollMode(
  isMaintenance: boolean,
  hasRecentFailure: boolean,
): ApiHealthPollMode {
  if (isMaintenance) {
    return 'unhealthy';
  }
  if (hasRecentFailure) {
    return 'checking';
  }
  return 'healthy';
}

const ApiHealthGate = ({ children }: ApiHealthGateProps) => {
  const gateEnabled = isApiHealthGateEnabled();
  const consecutiveFailuresRef = useRef(0);
  const lastProcessedUpdatedAtRef = useRef(0);
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [hasRecentFailure, setHasRecentFailure] = useState(false);

  const { data, isError, isFetched, dataUpdatedAt } = useApiHealth({
    enabled: gateEnabled,
    pollMode: getPollMode(isMaintenance, hasRecentFailure),
  });

  useEffect(() => {
    if (!gateEnabled) {
      setIsMaintenance(false);
      setHasRecentFailure(false);
      return;
    }

    if (!isFetched || !dataUpdatedAt) {
      return;
    }

    if (lastProcessedUpdatedAtRef.current === dataUpdatedAt) {
      return;
    }

    lastProcessedUpdatedAtRef.current = dataUpdatedAt;

    const isHealthy = isApiReady(data) && !isError;

    if (isHealthy) {
      consecutiveFailuresRef.current = 0;
      setIsMaintenance(false);
      setHasRecentFailure(false);
      return;
    }

    consecutiveFailuresRef.current += 1;
    setHasRecentFailure(true);

    if (consecutiveFailuresRef.current >= API_HEALTH_FAILURE_THRESHOLD) {
      setIsMaintenance(true);
    }
  }, [gateEnabled, data, isError, isFetched, dataUpdatedAt]);

  if (!gateEnabled) {
    return <>{children}</>;
  }

  if (isMaintenance) {
    return (
      <>
        <NativeShopShellReady />
        <InfraMaintenanceScreen />
      </>
    );
  }

  return <>{children}</>;
};

export default ApiHealthGate;
